import {createRouter, createWebHistory} from 'vue-router'
import { useGlobalStore } from "stores/global.js"
import { pageTitle } from 'utils/pageTitle.js'
import { isChunkLoadError, reloadForNewDeploy } from 'utils/chunkReload.js'

// Every page component is loaded on demand, so a visit downloads the code of the
// page it opens and nothing else. Keep new routes lazy too:
//   const MyPage = () => import("controllers/vue_components/my_page/MyPage.vue")
// A static import here pulls the page into the entry bundle for every visitor.
const AnalyticsPage = () => import("controllers/vue_components/analytics/AnalyticsPage")
const EventsPage = () => import("controllers/vue_components/events/EventsPage")
const EventLayout = () => import("controllers/vue_components/events/EventLayout")
const EventComingSoon = () => import("controllers/vue_components/events/EventComingSoon")
const EventBookingsPage = () => import("controllers/vue_components/events/bookings/EventBookingsPage")
const EventAttendeesPage = () => import("controllers/vue_components/events/attendees/EventAttendeesPage")
const EventEditor = () => import("controllers/vue_components/events/editor/EventEditorPage.vue")
const Availability = () => import("controllers/vue_components/calendar/availability")
const Holiday = () => import("controllers/vue_components/holiday")
const SettingsIndex = () => import("controllers/vue_components/settings/v1")
const OrganizationStructure = () => import("controllers/vue_components/organizations/structure/index.vue")
const OrganizationApplications = () => import("controllers/vue_components/organizations/applications/index.vue")
const OrganizationApplicationFactsheet = () => import("controllers/vue_components/organizations/applications/factsheet.vue")
const OrganizationItComponents = () => import("controllers/vue_components/organizations/it_components/index.vue")
const OrganizationDashboard = () => import("controllers/vue_components/organizations/dashboard")
const OrganizationEvents = () => import("controllers/vue_components/organizations/events")
const OrganizationBookings = () => import("controllers/vue_components/organizations/bookings")
const OrganizationCalendar = () => import("controllers/vue_components/organizations/calendar")
const OrganizationAnalytics = () => import("controllers/vue_components/organizations/analytics")
const OrganizationNotification = () => import("controllers/vue_components/organizations/notification")
const OrganizationProfile = () => import("controllers/vue_components/organizations/settings/profile")
const OrganizationSocialLinks = () => import("controllers/vue_components/organizations/settings/social_links")
const OrganizationMembers = () => import("controllers/vue_components/organizations/settings/members")
const OrganizationActivityLogs = () => import("controllers/vue_components/organizations/activity_logs")
const OrganizationPlan = () => import("controllers/vue_components/organizations/settings/plan")
const OrganizationDevelopers = () => import("controllers/vue_components/organizations/settings/developers")
const OrganizationAccount = () => import("controllers/vue_components/organizations/settings/account")
const OrganizationAvailabilities = () => import("controllers/vue_components/organizations/availabilities")
const ConnectedCalendars = () => import("controllers/vue_components/connected_calendars")
const IntegrationsPage = () => import("controllers/vue_components/integrations/IntegrationsPage")
const JobsPage = () => import("controllers/vue_components/jobs/JobsPage")
const JobNewPage = () => import("controllers/vue_components/jobs/JobNewPage.vue")
const JobCompaniesPage = () => import("controllers/vue_components/jobs/JobCompaniesPage.vue")
const ResumesPage = () => import("controllers/vue_components/resumes/ResumesPage.vue")
const ResumeEditorPage = () => import("controllers/vue_components/resumes/ResumeEditorPage.vue")
const JobTrackerPage = () => import("controllers/vue_components/integrations/JobTrackerPage")
const OpenAiPage = () => import("controllers/vue_components/integrations/OpenAiPage.vue")
const StripePage = () => import("controllers/vue_components/integrations/StripePage.vue")
const PaymentsPage = () => import("controllers/vue_components/payments/PaymentsPage.vue")
const ExtensionAuthorizePage = () => import("controllers/vue_components/integrations/ExtensionAuthorizePage")
const WebhooksPage = () => import("controllers/vue_components/integrations/WebhooksPage")
const ExternalPaymentsPage = () => import("controllers/vue_components/integrations/ExternalPaymentsPage")
const QuestionsPage = () => import("controllers/vue_components/questions/QuestionsPage")
const AttendeesList = () => import("controllers/vue_components/events/booking/attendees/list")
const NotificationsPage = () => import("controllers/vue_components/notifications/NotificationsPage")
const TasksPage = () => import("controllers/vue_components/tasks/TasksPage.vue")
const TaskDetailPage = () => import("controllers/vue_components/tasks/TaskDetailPage.vue")

const Individuals = () => import("controllers/vue_components/pricing/individuals")
const TeamsAndOrganizations = () => import("controllers/vue_components/pricing/teams_and_org")
const BuildWithTimelydo = () => import("controllers/vue_components/pricing/build_with_timelydo")
const UserInvitationsPage = () => import("controllers/vue_components/admin/user_invitations/UserInvitationsPage.vue")
const GlobalComponentsPage = () => import("controllers/vue_components/admin/global_components/GlobalComponentsPage.vue")
const ReleaseNotesPage = () => import("controllers/vue_components/admin/release_notes/ReleaseNotesPage.vue")
const CareerJobsPage = () => import("controllers/vue_components/admin/career_jobs/CareerJobsPage.vue")
const AdminHellobarPage = () => import("controllers/vue_components/admin/hellobar/AdminHellobarPage.vue")
const AdminServerLogsPage = () => import("controllers/vue_components/admin/server_logs/AdminServerLogsPage.vue")
const AdminRailsConsolePage = () => import("controllers/vue_components/admin/rails_console/AdminRailsConsolePage.vue")
const AdminUsersList = () => import("controllers/vue_components/admin/users/list")
const AdminEmails = () => import("controllers/vue_components/admin/emails/emails")
const AdminVerificationsList = () => import("controllers/vue_components/admin/verifications/list")
const AdminBugsList = () => import("controllers/vue_components/admin/bugs/list")
const CalendarPage = () => import("controllers/vue_components/calendar/CalendarPage.vue")

const routes = [
    {
        path: '/calendar',
        name: 'Calendar',
        component: CalendarPage,
        meta: { title: 'Calendar' },
    },
    {
        // /analytics is a section index only. Bookings is its landing page.
        path: '/analytics',
        redirect: '/analytics/bookings',
    },
    {
        path: '/analytics/bookings',
        name: 'AnalyticsBookings',
        component: AnalyticsPage,
        meta: { title: 'Bookings | Analytics' },
    },
    {
        path: '/analytics/tasks',
        name: 'AnalyticsTasks',
        component: AnalyticsPage,
        meta: { title: 'Tasks | Analytics' },
    },
    {
        path: '/analytics/traffic',
        name: 'AnalyticsTraffic',
        component: AnalyticsPage,
        meta: { title: 'Traffic | Analytics' },
    },
    {
        path: '/notifications',
        name: 'Notifications',
        component: NotificationsPage,
        meta: { title: 'Notifications' },
    },
    {
        path: '/notifications/visits',
        name: 'NotificationVisits',
        component: NotificationsPage,
        meta: { title: 'Visits | Notifications' },
    },
    {
        path: '/notifications/messages',
        name: 'NotificationMessages',
        component: NotificationsPage,
        meta: { title: 'Messages | Notifications' },
    },
    {
        path: '/connected_calendars',
        name: 'ConnectedCalendars',
        component: ConnectedCalendars,
        meta: { title: 'Connected Calendars' },
    },
    {
        path: '/jobs',
        name: 'Jobs',
        component: JobsPage,
        meta: { title: 'Job Listings', requiresIntegration: 'job_tracker' },
    },
    {
        path: '/jobs/new',
        name: 'JobNew',
        component: JobNewPage,
        meta: { title: 'Add a job | Jobs', requiresIntegration: 'job_tracker' },
    },
    {
        path: '/companies',
        name: 'JobCompanies',
        component: JobCompaniesPage,
        meta: { title: 'Companies | Jobs', requiresIntegration: 'job_tracker' },
    },
    {
        path: '/cv_builder',
        name: 'CvBuilder',
        component: ResumesPage,
        meta: { title: 'CV Builder | Jobs', requiresIntegration: 'job_tracker' },
    },
    {
        path: '/cv_builder/:id',
        name: 'CvBuilderEditor',
        component: ResumeEditorPage,
        meta: { title: 'Edit resume | CV Builder', requiresIntegration: 'job_tracker' },
    },
    {
        path: '/integrations',
        name: 'Integrations',
        component: IntegrationsPage,
        meta: { title: 'Integrations' },
    },
    {
        path: '/extension/authorize',
        name: 'ExtensionAuthorize',
        component: ExtensionAuthorizePage,
        meta: { title: 'Connect Timelydo Jobs' },
    },
    {
        path: '/integrations/openai',
        name: 'IntegrationsOpenAi',
        component: OpenAiPage,
        meta: { title: 'OpenAI | Integrations' },
    },
    {
        path: '/payments',
        name: 'Payments',
        component: PaymentsPage,
        meta: { title: 'Payments' },
    },
    {
        path: '/integrations/stripe',
        name: 'IntegrationsStripe',
        component: StripePage,
        meta: { title: 'Stripe | Integrations' },
    },
    {
        path: '/integrations/job_tracker',
        name: 'JobTrackerIntegration',
        component: JobTrackerPage,
        meta: { title: 'Job Tracker | Integrations' },
    },
    {
        path: '/integrations/webhooks',
        name: 'Webhooks',
        component: WebhooksPage,
        meta: { title: 'Webhooks | Integrations' },
    },
    {
        path: '/integrations/external_payments',
        name: 'ExternalPayments',
        component: ExternalPaymentsPage,
        meta: { title: 'External Payments | Integrations' },
    },
    {
        path: '/questions',
        name: 'Questions',
        component: QuestionsPage,
        meta: { title: 'Form Questions' },
    },
    {
        path: '/attendees',
        name: 'Attendees',
        component: AttendeesList,
        meta: { title: 'Attendees' },
    },
    {
        path: '/availabilities',
        name: 'Availability',
        component: Availability,
        meta: { title: 'Availabilities' },
    },
    {
        path: '/holidays',
        name: 'Holidays',
        component: Holiday,
        meta: { title: 'Holidays' },
    },
    {
        path: '/tasks',
        name: 'Tasks',
        component: TasksPage,
        meta: { title: 'Tasks' },
        // The Stats tab moved to /analytics/tasks. Old links keep every filter
        // they carried; only the `tab` key is dropped. Rails redirects the same
        // shape for a cold load, this covers SPA navigation.
        beforeEnter: (to) => {
            if (to.query.tab !== 'stats') return
            const { tab, scope, ...filters } = to.query
            return { path: '/analytics/tasks', query: filters }
        },
    },
    {
        path: '/tasks/:id',
        name: 'TaskDetail',
        component: TaskDetailPage,
        meta: { title: 'Task | Tasks' },
    },
    {
        path: '/events',
        name: 'Events',
        component: EventsPage,
        meta: { title: 'Events' },
    },
    {
        path: '/events/new',
        name: 'NewEvent',
        component: EventEditor,
        meta: { title: 'New Event | Events' },
    },
    {
        path: '/events/:id/edit',
        name: 'EditEvent',
        component: EventEditor,
        meta: { title: 'Edit Event | Events' },
    },
    // One layout for every per-event tab: it loads the event once and keeps
    // EventSubNav mounted while the child tab swaps underneath it.
    // `meta.eventTab` tells the nav which tab to highlight. A bare /events/:id
    // cold load never reaches Vue (Rails redirects it to the public page), so the
    // empty child only covers in-app navigation.
    {
        path: '/events/:id',
        component: EventLayout,
        children: [
            { path: '', redirect: (to) => `/events/${to.params.id}/bookings` },
            { path: 'bookings',    name: 'EventBookings',   component: EventBookingsPage,  meta: { title: 'Bookings | Events',    eventTab: 'bookings' } },
            { path: 'attendees',   name: 'EventAttendees',  component: EventAttendeesPage, meta: { title: 'Attendees | Events',   eventTab: 'attendees' } },
            { path: 'analytics',   name: 'EventAnalytics',  component: EventComingSoon, props: { feature: 'analytics' },   meta: { title: 'Analytics | Events',   eventTab: 'analytics' } },
            { path: 'qr_code',     name: 'EventQrCode',     component: EventComingSoon, props: { feature: 'qr_code' },     meta: { title: 'QR Code | Events',     eventTab: 'qr_code' } },
            { path: 'embed',       name: 'EventEmbed',      component: EventComingSoon, props: { feature: 'embed' },       meta: { title: 'Embed Code | Events',  eventTab: 'embed' } },
            { path: 'link_button', name: 'EventLinkButton', component: EventComingSoon, props: { feature: 'link_button' }, meta: { title: 'Link Button | Events', eventTab: 'link_button' } },
        ],
    },
    { path: '/settings/profile/general',      component: SettingsIndex, meta: { title: 'General | Settings' } },
    { path: '/settings/profile/branding',     component: SettingsIndex, meta: { title: 'Branding | Settings' } },
    { path: '/settings/profile/themes',       component: SettingsIndex, meta: { title: 'Themes | Settings' } },
    { path: '/settings/profile/social_links', component: SettingsIndex, meta: { title: 'Social Links | Settings' } },
    { path: '/settings/profile/policy',       component: SettingsIndex, meta: { title: 'Custom Policy | Settings' } },
    { path: '/settings/profile/verification', component: SettingsIndex, meta: { title: 'Get Verified | Settings' } },
    { path: '/settings/account',              component: SettingsIndex, meta: { title: 'Account | Settings' } },
    { path: '/settings/appearance',           component: SettingsIndex, meta: { title: 'Appearance | Settings' } },
    { path: '/settings/security',             component: SettingsIndex, meta: { title: 'Security | Settings' } },
    { path: '/settings/developers/api_key',   component: SettingsIndex, meta: { title: 'API Key | Settings' } },
    { path: '/settings/developers/embed_code',component: SettingsIndex, meta: { title: 'Embed Code | Settings' } },
    { path: '/settings/developers/qr_code',   component: SettingsIndex, meta: { title: 'QR Code | Settings' } },
    { path: '/settings/:section(.*)*', name: 'Settings', component: SettingsIndex, meta: { title: 'Settings' } },
    // ORGANIZATION ROUTES

    {
        path: '/organizations/:url/',
        children: [
            {
                path: 'structure',
                name: 'OrganizationStructure',
                component: OrganizationStructure,
                meta: { title: 'Structure | Organization' },
            },

            {
                path: 'applications',
                name: 'OrganizationApplications',
                component: OrganizationApplications,
                meta: { title: 'Applications | Organization' },
            },

            {
                path: 'applications/:id',
                name: 'OrganizationApplicationFactsheet',
                component: OrganizationApplicationFactsheet,
                meta: { title: 'Application Factsheet' },
            },

            {
                path: 'it_components',
                name: 'OrganizationItComponents',
                component: OrganizationItComponents,
                meta: { title: 'IT Components | Organization' },
            },

            {
                path: 'profile',
                component: OrganizationProfile,
                meta: { title: 'Profile | Organization' },
            },

            {
                path: 'profile/social_links',
                component: OrganizationSocialLinks,
                meta: { title: 'Social Links | Organization' },
            },

            {
                path: 'account',
                component: OrganizationAccount,
                meta: { title: 'Account | Organization' },
            },

            {
                path: 'members',
                component: OrganizationMembers,
                meta: { title: 'Members | Organization' },
            },

            {
                path: 'plan',
                component: OrganizationPlan,
                meta: { title: 'Plan | Organization' },
            },
        
            {
                path: 'developers',
                component: OrganizationDevelopers,
                meta: { title: 'Developers | Organization' },
            },

            {
                path: 'dashboard',
                name: 'OrganizationDashboard',
                component: OrganizationDashboard,
                meta: { title: 'Dashboard | Organization' }
            },

            {
                path: 'activity_logs',
                name: 'OrganizationActivityLogs',
                component: OrganizationActivityLogs,
                meta: { title: 'Activity Logs | Organization' },
            },

            {
                path: 'events',
                name: 'OrganizationEvents',
                component: OrganizationEvents,
                meta: { title: 'Events | Organization' }
            },
        
            {
                path: 'bookings',
                name: 'OrganizationBookings',
                component: OrganizationBookings,
                meta: { title: 'Bookings | Organization' }
            },
        
            {
                path: 'calendar',
                name: 'OrganizationCalendar',
                component: OrganizationCalendar,
                meta: { title: 'Calendar | Organization' }
            },
        
            {
                path: 'analytics',
                name: 'OrganizationAnalytics',
                component: OrganizationAnalytics,
                meta: { title: 'Analytics | Organization' }
            },
        
            {
                path: 'notifications',
                name: 'OrganizationNotification',
                component: OrganizationNotification,
                meta: { title: 'Notifications | Organization' }
            },
        
            {
                path: 'availabilities',
                name: 'OrganizationAvailabilities',
                component: OrganizationAvailabilities,
                meta: { title: 'Availabilities | Organization' }
            },
        ],
    },

    // Pricing routes

    {
        path: '/pricing/individuals',
        name: 'Individuals',
        component: Individuals,
        meta: { title: 'Individuals | Pricing' },
    },

    {
        path: '/pricing/teams-and-organizations',
        name: 'TeamsAndOrganizations',
        component: TeamsAndOrganizations,
        meta: { title: 'Teams And Organizations | Pricing' },
    },

    {
        path: '/pricing/build-with-timelydo',
        name: 'BuildWithTimelydo',
        component: BuildWithTimelydo,
        meta: { title: 'Build With Timelydo | Pricing' },
    },
    {
        path: '/admin/user_invitations',
        name: 'AdminUserInvitations',
        component: UserInvitationsPage,
        meta: { title: 'User Invitations | Admin' },
    },
    {
        path: '/admin/global_components',
        name: 'AdminGlobalComponents',
        component: GlobalComponentsPage,
        meta: { title: 'Global IT Components | Admin' },
    },
    {
        path: '/admin/release_notes',
        name: 'AdminReleaseNotes',
        component: ReleaseNotesPage,
        meta: { title: 'Release Notes | Admin' },
    },
    {
        path: '/admin/career_jobs',
        name: 'AdminCareerJobs',
        component: CareerJobsPage,
        meta: { title: 'Career Jobs | Admin' },
    },
    {
        path: '/admin/hellobar',
        name: 'AdminHellobar',
        component: AdminHellobarPage,
        meta: { title: 'Hellobar | Admin' },
    },
    {
        path: '/admin/server_logs',
        name: 'AdminServerLogs',
        component: AdminServerLogsPage,
        meta: { title: 'Server Logs | Admin' },
    },
    {
        path: '/admin/rails_console',
        name: 'AdminRailsConsole',
        component: AdminRailsConsolePage,
        meta: { title: 'Rails Console | Admin' },
    },
    {
        path: '/admin/users',
        name: 'AdminUsers',
        component: AdminUsersList,
        meta: { title: 'Users | Admin' },
    },
    {
        path: '/admin/emails',
        name: 'AdminEmails',
        component: AdminEmails,
        meta: { title: 'Emails | Admin' },
    },
    {
        path: '/admin/verifications',
        name: 'AdminVerifications',
        component: AdminVerificationsList,
        meta: { title: 'Verifications | Admin' },
    },
    {
        path: '/admin/bugs',
        name: 'AdminBugs',
        component: AdminBugsList,
        meta: { title: 'Bug Reports | Admin' },
    },

    // Catch-all: Rails handles all other routes — render nothing and suppress the no-match warning
    { path: '/:pathMatch(.*)*', component: { render: () => null } },
]

const router = createRouter({
    history: createWebHistory('/'),
    routes,
})

// `meta.title` holds the trail only ("Bookings | Analytics"); the brand suffix
// is appended here so it lives in one place rather than in every route.
router.beforeEach((to, from, next) => {
    if (to.meta.title !== undefined) {
        document.title = pageTitle(to.meta.title)
    }

    // Pages that belong to an integration are only reachable once it is switched on.
    if (to.meta.requiresIntegration && !useGlobalStore().hasIntegration(to.meta.requiresIntegration)) {
        return next('/integrations');
    }

    next();
});

// A tab left open across a deploy still points at chunk files the new build no longer
// has. The first time that happens, reload the page being opened so it gets the new build.
router.onError((error, to) => {
    if (isChunkLoadError(error)) reloadForNewDeploy(to?.fullPath)
})

export default router